"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

<Helmet>
  <title>Listings — Hotels, Plazas & Land for Sale in Canada | Maoq Digital</title>
  <meta name="description" content="Latest opportunities across Canada. Branded hotels, neighbourhood plazas, and development land. Clean information, private process." />
  <link rel="canonical" href="https://www.maoqdigital.com/listings" />

  <meta property="og:type" content="website" />
  <meta property="og:title" content="Listings — Hotels, Plazas & Land | Maoq Digital" />
  <meta property="og:description" content="Explore current hotel, plaza, and land opportunities across Canada." />
  <meta property="og:url" content="https://www.maoqdigital.com/listings" />
  <meta property="og:image" content="https://www.maoqdigital.com/images/linkpreview.png" />
  <meta property="og:image:alt" content="Commercial listings in Canada" />

  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content="Listings — Hotels, Plazas & Land | Maoq Digital" />
  <meta name="twitter:description" content="Explore current hotel, plaza, and land opportunities across Canada." />
  <meta name="twitter:image" content="https://www.maoqdigital.com/images/linkpreview.png" />

  {/* JSON-LD: CollectionPage + SearchAction */}
  <script type="application/ld+json">{JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Commercial Real Estate Listings — Hotels, Plazas & Land",
    "url": "https://www.maoqdigital.com/listings",
    "isPartOf": { "@type": "WebSite", "url": "https://www.maoqdigital.com/" },
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://www.maoqdigital.com/listings?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  })}</script>
</Helmet>

/** ===========================================================
 *  CONFIG — Your Google Sheets "Publish to web" CSV URL
 *  Header row must be EXACTLY:
 *  title,type,province,location,price,image_url,tags,posted_at
 *  Dates as YYYY-MM-DD (e.g., 2025-10-14)
 * =========================================================== */
const SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/e/2PACX-1vT-YQUJcltTZ6EKNk_-LJQCFY2k2292go-9xDiLKupi4UrktEwxWtpYwv9iHEn4SmUBWxVcbdc-bdRn/pub?output=csv";

/* ================= CSV → JSON helpers (handles quoted commas) ================= */
function splitCsv(line) {
  const out = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}

function csvToJson(csv) {
  const lines = csv.trim().split(/\r?\n/);
  if (!lines.length) return [];
  const headers = splitCsv(lines[0]).map((h) => h.trim());

  return lines
    .slice(1)
    .filter((l) => l.trim().length)
    .map((line) => {
      const cells = splitCsv(line).map((c) => c.trim());
      const row = {};
      headers.forEach((h, i) => (row[h] = cells[i] ?? ""));
      return row;
    });
}

/* =================== small helpers =================== */
const toNumber = (v) => {
  if (v == null) return null;
  if (typeof v === "number") return v;
  const s = String(v).replace(/[,$\s]/g, "");
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

const currencyFmt = (v) =>
  v == null ? "—" : v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${v.toLocaleString()}`;

const dateFmt = (d) => {
  if (!d) return "—";
  const x = new Date(d);
  if (isNaN(x.getTime())) return d; // show raw if not a real date
  return x.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-medium text-gray-700 ${className}`}
  >
    {children}
  </span>
);

const Button = ({ children, className = "", as = "a", href = "#", onClick }) => {
  const classes = `
    inline-flex items-center justify-center rounded-xl h-10 px-4
    bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition
    ${className}
  `;
  return as === "button" ? (
    <button className={classes} onClick={onClick}>
      {children}
    </button>
  ) : (
    <a className={classes} href={href}>
      {children}
    </a>
  );
};

/* =================== Listing Tile / Card =================== */
const ListingCard = ({ item }) => {
  const { title, type, location, province, price, image_url, tags, posted_at } = item;

  const tagList = Array.isArray(tags)
    ? tags
    : typeof tags === "string" && tags.length
    ? tags.split(",").map((s) => s.trim()).slice(0, 4)
    : [];

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition">
      <img
        src={
          image_url ||
          "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1200&auto=format&fit=crop"
        }
        alt={title || "Listing"}
        className="h-48 w-full object-cover"
        loading="lazy"
      />
      <div className="p-4 flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="heading-serif text-lg leading-snug text-black">
            {title || "Untitled"}
          </h3>
          <Badge>{province || "CA"}</Badge>
        </div>

        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Badge className="border-blue-200 bg-blue-50 text-blue-700">
            {type || "Asset"}
          </Badge>
          <span className="truncate">{location}</span>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-1">
          <div className="rounded-md border border-gray-100 bg-gray-50 p-2">
            <p className="text-[11px] text-gray-500">Price</p>
            <p className="text-sm font-semibold text-gray-900">
              {currencyFmt(toNumber(price))}
            </p>
          </div>
          <div className="rounded-md border border-gray-100 bg-gray-50 p-2">
            <p className="text-[11px] text-gray-500">Posted</p>
            <p className="text-sm font-semibold text-gray-900">{dateFmt(posted_at)}</p>
          </div>
        </div>

        {!!tagList.length && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {tagList.map((t, i) => (
              <Badge key={i}>{t}</Badge>
            ))}
          </div>
        )}

        <div className="mt-3 flex gap-2">
          <Button href="/contact">Contact</Button>
          <a
            href="mailto:aumthakar@maoqdigital.com"
            className="inline-flex items-center justify-center rounded-xl h-10 px-4 border border-gray-300 text-sm font-semibold text-gray-800 hover:bg-gray-50"
          >
            Email
          </a>
        </div>
      </div>
    </div>
  );
};

/* =================== Light Filters Bar =================== */
const FiltersBar = ({ q, setQ, type, setType, province, setProvince, reset }) => {
  const types = ["All", "Hotel", "Plaza", "Land", "Construction"];
  const provinces = ["All", "ON", "BC", "AB", "QC", "MB", "SK", "NS", "NB", "NL", "PE", "YT", "NT", "NU"];

  return (
    <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full">
        <input
          placeholder="Search title or city…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="col-span-2 h-10 rounded-xl border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="h-10 rounded-xl border border-gray-300 px-3 text-sm"
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        <select
          value={province}
          onChange={(e) => setProvince(e.target.value)}
          className="h-10 rounded-xl border border-gray-300 px-3 text-sm"
        >
          {provinces.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <button
        onClick={reset}
        className="h-10 rounded-xl border border-gray-300 px-4 text-sm font-semibold hover:bg-gray-50"
      >
        Reset
      </button>
    </div>
  );
};

/* =================== Page =================== */
const Listings = () => {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // light controls
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [province, setProvince] = useState("All");

  // pagination
  const [visible, setVisible] = useState(12);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setErr("");

    fetch(SHEET_CSV_URL)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text(); // CSV, not JSON
      })
      .then((txt) => {
        if (!isMounted) return;
        const rows = csvToJson(txt);
        setRaw(rows);
        setLoading(false);
      })
      .catch((e) => {
        if (!isMounted) return;
        console.error(e);
        setErr("Could not load listings. Please try again.");
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const reset = () => {
    setQ("");
    setType("All");
    setProvince("All");
    setVisible(12);
  };

  // filter + sort (newest by posted_at)
  const data = useMemo(() => {
    let list = raw.slice();

    // Normalize field names defensively (in case headers had spaces/case)
    list = list.map((r) => ({
      title: r.title ?? r.Title ?? "",
      type: r.type ?? r.Type ?? "",
      province: r.province ?? r.Province ?? "",
      location: r.location ?? r.Location ?? "",
      price: r.price ?? r.Price ?? "",
      image_url: r.image_url ?? r.Image ?? r.ImageURL ?? "",
      tags: r.tags ?? r.Tags ?? "",
      posted_at: r.posted_at ?? r.Posted ?? r.PostedAt ?? "",
    }));

    if (q.trim()) {
      const k = q.trim().toLowerCase();
      list = list.filter(
        (r) =>
          String(r.title || "").toLowerCase().includes(k) ||
          String(r.location || "").toLowerCase().includes(k)
      );
    }
    if (type !== "All") {
      list = list.filter(
        (r) => String(r.type || "").toLowerCase() === type.toLowerCase()
      );
    }
    if (province !== "All") {
      list = list.filter(
        (r) => String(r.province || "").toUpperCase() === province.toUpperCase()
      );
    }

    // newest first by posted_at if present
    list.sort(
      (a, b) =>
        (Date.parse(b.posted_at || "") || 0) - (Date.parse(a.posted_at || "") || 0)
    );

    return list;
  }, [raw, q, type, province]);

  const toShow = data.slice(0, visible);

  return (
    <div
      className="relative flex min-h-screen flex-col bg-white overflow-x-hidden"
      style={{ fontFamily: 'Manrope, "Noto Sans", sans-serif' }}
    >
      <div className="flex flex-col grow h-full">
        <div className="flex flex-1 justify-center py-8 px-4 md:px-40">
          <div className="flex flex-col w-full max-w-6xl gap-8">
            {/* Header / Hero-lite */}
            <section className="text-center">
              <h1 className="heading-serif text-3xl sm:text-4xl font-normal tracking-tight text-black">
                Listings
              </h1>
              <p className="text-gray-700 mt-2">
                Hotels, plazas, land, and construction across Canada.
              </p>
            </section>

            {/* Filters */}
            <section className="rounded-2xl border border-white-200 bg-white p-4 shadow-sm">
              <FiltersBar
                q={q}
                setQ={setQ}
                type={type}
                setType={setType}
                province={province}
                setProvince={setProvince}
                reset={reset}
              />
            </section>

            {/* Grid / Tiles */}
            <section>
              {loading && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse h-80 rounded-2xl bg-gray-100"
                    />
                  ))}
                </div>
              )}

              {err && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
                  {err}
                </div>
              )}

              {!loading && !err && toShow.length === 0 && (
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-center text-gray-700">
                  No matching listings. Try different filters.
                </div>
              )}

              {!loading && !err && toShow.length > 0 && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {toShow.map((item, idx) => (
                      <ListingCard key={`${item.title}-${idx}`} item={item} />
                    ))}
                  </div>

                  {visible < data.length && (
                    <div className="flex justify-center mt-8">
                      <Button as="button" onClick={() => setVisible((v) => v + 12)}>
                        Load more
                      </Button>
                    </div>
                  )}
                </>
              )}
            </section>

    
          </div>
        </div>
      </div>
    </div>
  );
};

export default Listings;
